/**
 * Centralized app-origin resolution (JavaScript version for build tools)
 * Single source of truth for "which origin is NG Meet actually running from?"
 *
 * SM-1243: Environment detection MUST be derived from the origin the NG Meet
 * BUNDLE was served from — NOT from `window.location`. In Module Federation /
 * TeamHub hosted mode the app runs on the HOST's page (e.g.
 * `teamhub.sangoma.com`), so `window.location.hostname` never matches our
 * hostname allowlists and every environment check silently falls through to its
 * non-production default (appdev CPaaS, test observer, develop Jitsi app id).
 *
 * `__webpack_public_path__` is the rspack runtime global. In MF mode the rspack
 * config sets `output.publicPath: 'auto'`, which resolves at runtime to the
 * origin the bundle was served from (e.g. `https://meetings.sangoma.com/mf/`).
 * In standalone builds the public path is `/`, which resolves back to the
 * current window origin — so this is a no-op there.
 *
 * The rspack runtime assigns `__webpack_public_path__` before any application
 * module is evaluated, which is why this — and not `isHostedByTeamHub()` — is
 * the signal used here: config modules freeze their environment in module-level
 * constants at import time, long before `setFederatedMode()` runs in `mount()`.
 *
 * Kept as dependency-free plain JS because `rspack.config.js` imports sibling
 * config modules directly in Node at build time, where `window` does not exist.
 */

/**
 * The URL the NG Meet bundle was served from, or `null` when it can't be
 * resolved (no public path, malformed public path, or non-browser environment).
 *
 * @returns {URL | null}
 */
function resolveBundleUrl() {
  if (typeof window === 'undefined' || !window.location) {
    return null;
  }

  try {
    if (typeof __webpack_public_path__ === 'string' && __webpack_public_path__) {
      return new URL(__webpack_public_path__, window.location.href);
    }
  } catch {
    // Malformed public path — callers fall back to the window location.
  }

  return null;
}

/**
 * NG Meet's OWN origin, strictly from the bundle's public path.
 *
 * Returns `null` rather than falling back to the window origin, so callers can
 * distinguish "the bundle told us where it came from" from "we have no idea".
 * Use this when a window-origin fallback would be wrong (e.g. it would hand out
 * the TeamHub origin in hosted mode).
 *
 * @returns {string | null}
 */
export function resolveBundleOrigin() {
  const bundleUrl = resolveBundleUrl();
  return bundleUrl ? bundleUrl.origin : null;
}

/**
 * NG Meet's own origin, falling back to the current window origin.
 *
 * @returns {string | null} `null` only outside a browser.
 */
export function resolveAppOrigin() {
  const bundleUrl = resolveBundleUrl();
  if (bundleUrl) {
    return bundleUrl.origin;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return null;
}

/**
 * The hostname NG Meet should derive its environment from.
 *
 * This is the value to match against hostname allowlists (production domains,
 * observer sites, Jitsi app ids) instead of `window.location.hostname`.
 *
 * @returns {string | null} `null` only outside a browser.
 */
export function resolveAppHostname() {
  const bundleUrl = resolveBundleUrl();
  if (bundleUrl) {
    return bundleUrl.hostname;
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }

  return null;
}
